import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import { PieChart, Pie, Cell, Tooltip } from 'recharts'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { KpiRow } from './Kpi'
import { Swatch, TooltipCard } from './ChartParts'
import { rankGroups } from './measures'
import { groupName } from './HorizontalBarWidget'
import useAnalyticsWidget from './useAnalyticsWidget'
import useElementSize from './useElementSize'

/*
 * How a total divides: a ring of categories, the total in its middle, and the
 * categories ranked beside it with their share.
 *
 *     breakdown: { value, limit: 5, other, labels, emptyLabel }  — as the horizontal bar
 *     center: { label }       the word under the total ("Total", "Users")
 *     showValues              the amount beside each share in the list
 *     colors: { group: token } a fixed colour for a group — 'desktop' is always blue
 *
 * Colours follow rank, and the rank order is fixed (largest first, ties by
 * name), so the same period always paints the same; a group that must keep its
 * colour across periods gets one in `colors`. Hovering or focusing a row lifts
 * its slice. On a card narrower than it is wide enough for both, the list moves
 * under the ring.
 */

const Layout = styled.div`
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    align-items: center;
    gap: 0.75rem;

    @container analytics (max-width: 340px) {
        grid-template-columns: 1fr;
        grid-template-rows: minmax(110px, 1fr) auto;
    }
`

const RingBox = styled.div`
    position: relative;
    height: 100%;
    min-height: 110px;

    & > div { position: absolute; inset: 0; }
`

const Center = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    text-align: center;

    strong {
        ${widgetFont.value}
        font-variant-numeric: tabular-nums;
        line-height: 1.15;
    }
    span {
        ${widgetFont.caption}
        color: ${p => p.theme.font.label};
    }
`

const Breakdown = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
    overflow-y: auto;
    max-height: 100%;
`

const Item = styled.li`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem;
    padding: 0.2rem 0.3rem;
    border-radius: 0.375rem;
    ${widgetFont.body}
    background: ${p => p.$active ? p.theme.table.hover : 'transparent'};

    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: -2px; }

    .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .share { font-weight: 600; font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
    .share small { font-weight: 400; color: ${p => p.theme.font.label}; margin-right: 0.4rem; }
`

const DonutWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format } = w
    const [ref, size] = useElementSize()
    const [active, setActive] = useState(null)
    const breakdown = config.breakdown ?? {}
    const valueFormat = config.measures?.[breakdown.value]?.format ?? 'number'

    const { slices, total } = useMemo(() => {
        if (!data.current) return { slices: [], total: 0 }
        const groups = data.current.groups(breakdown.value)
        const total = groups.reduce((s, g) => s + g.value, 0)
        const ranked = rankGroups(groups.filter(g => g.value > 0), { limit: breakdown.limit ?? 5, other: breakdown.other !== false })
        return {
            total,
            slices: ranked.map((g, i) => ({
                ...g,
                id: String(g.key),
                name: groupName(g, breakdown, t),
                share: total > 0 ? (g.value / total) * 100 : 0,
                color: g.other ? palette.tokens.neutral : palette.color(config.colors?.[g.key], i),
            })),
        }
    }, [data.current, breakdown, config.colors, palette, t])

    const diameter = Math.min(size.width, size.height)
    const outer = Math.max(30, diameter / 2 - 4)
    const inner = outer * 0.68

    const renderTooltip = ({ active: on, payload }) => {
        if (!on || !payload?.length) return null
        const s = payload[0].payload
        return <TooltipCard title={s.name} rows={[
            { color: s.color, name: t('analytics.amount'), value: format(s.value, valueFormat) },
            { name: t('analytics.share'), value: format(s.share, 'percent') },
        ]} />
    }

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            path={config.path}
            status={w.status === null && total <= 0 ? 'empty' : w.status}
            error={data.error}
            onRetry={data.refetch}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            <Layout>
                <RingBox>
                    <div ref={ref}>
                        {size.width > 0 && size.height > 0 && (
                            <PieChart width={size.width} height={size.height} accessibilityLayer>
                                <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                    innerRadius={inner} outerRadius={outer} paddingAngle={slices.length > 1 ? 1.5 : 0}
                                    startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}
                                    onMouseEnter={(_, i) => setActive(i)} onMouseLeave={() => setActive(null)}>
                                    {slices.map((s, i) => (
                                        <Cell key={s.id} fill={s.color}
                                            fillOpacity={active == null || active === i ? 1 : 0.35} />
                                    ))}
                                </Pie>
                                {/* Above the total in the middle and the list beside the
                                    ring, which come later in the page and would
                                    otherwise paint over it */}
                                <Tooltip content={renderTooltip} wrapperStyle={{ zIndex: 2 }} />
                            </PieChart>
                        )}
                    </div>
                    <Center>
                        <strong>{format(total, valueFormat, { compact: diameter < 170 })}</strong>
                        <span>{t(config.center?.label ?? 'analytics.total')}</span>
                    </Center>
                </RingBox>
                <Breakdown>
                    {slices.map((s, i) => (
                        <Item key={s.id} $active={active === i} tabIndex={0}
                            onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
                            onFocus={() => setActive(i)} onBlur={() => setActive(null)}
                            title={`${s.name}: ${format(s.value, valueFormat)}`}>
                            <Swatch $color={s.color} />
                            <span className="name">{s.name}</span>
                            <span className="share">
                                {config.showValues && <small>{format(s.value, valueFormat, { compact: true })}</small>}
                                {format(s.share, 'percent')}
                            </span>
                        </Item>
                    ))}
                </Breakdown>
            </Layout>
        </AnalyticsCard>
    )
}

export default DonutWidget
